// src/services/frameworkDefinitionService.js
import api from '../api/apiClient'; // Adjust the path based on your structure
import i18n from '../i18n';

const BASE_URL = '/framework-definitions'; // Base path for these endpoints

/**
 * Apply language-specific transformations to framework data
 * @param {Object} framework - The framework object
 * @param {string} language - The target language ('en' or 'fr')
 * @returns {Object} - Transformed framework object
 */
const applyLanguageTransformations = (framework, language) => {
    // Create a copy to avoid mutating original data
    const transformedFramework = { ...framework };

    // Apply language-specific transformations based on framework type
    if (framework.type === 'cis') {
        transformedFramework.name = language === 'en' ? 'CIS Controls V8' : 'Contrôles CIS V8';
        transformedFramework.description = language === 'en'
            ? 'Center for Internet Security Controls Version 8'
            : 'Contrôles du Centre pour la Sécurité Internet Version 8';
    } else if (framework.type === 'nist') {
        transformedFramework.name = language === 'en' ? 'NIST CSF 2.0' : 'NIST CSF 2.0 (Cadre de Cybersécurité)';
        transformedFramework.description = language === 'en'
            ? 'NIST Cybersecurity Framework Version 2.0'
            : 'Cadre de Cybersécurité NIST Version 2.0';
    } else if (framework.type === 'iso27002') {
        // ISO 27002 frameworks already have language-specific names and descriptions
        // Keep the original name and description from the database
    } else if (framework.type === 'anssi') {
        // ANSSI frameworks already have language-specific names and descriptions
        // Keep the original name and description from the database
    }

    return transformedFramework;
};

/**
 * Fetches framework definitions (predefined + custom) for a specific analysis.
 * @param {string} analysisId - The ID of the analysis.
 * @param {string} language - Optional language override
 * @returns {Promise<Object>} - The API response (might contain { success, data, message }).
 */
const getFrameworkDefinitions = async (analysisId, language = null) => {
    if (!analysisId) {
        console.error("getFrameworkDefinitions requires an analysisId.");
        // Return a consistent error format or throw?
        return { success: false, message: "Analysis ID is required." };
    }
    try {
        const currentLanguage = language || i18n.language || 'fr';
        console.log(`[FrameworkService] Fetching framework definitions for language: ${currentLanguage}`);

        // CORRECTED: Pass parameters directly, not nested under 'params'
        const response = await api.get(BASE_URL, { analysisId });

        // Apply language-specific filtering and transformations
        if (response.data && Array.isArray(response.data)) {
            console.log(`[FrameworkService] Found ${response.data.length} total frameworks`);
            console.log(`[FrameworkService] Filtering for language: ${currentLanguage}`);

            // Group frameworks by type to select the best language match, but keep customs separate
            const frameworksByType = {};
            const customFrameworks = [];
            response.data.forEach(framework => {
                const type = framework.type || 'unknown';
                const isCustom = !framework.is_predefined || type === 'custom';
                if (isCustom) {
                    customFrameworks.push(framework);
                    console.log(`[FrameworkService] Found custom framework: ${framework.name} (${framework.language || 'no language'}) [${type}]`);
                    return;
                }
                if (!frameworksByType[type]) {
                    frameworksByType[type] = [];
                }
                frameworksByType[type].push(framework);
                console.log(`[FrameworkService] Found framework: ${framework.name} (${framework.language || 'no language'}) [${type}]`);
            });

            // Select the best language match for each framework type
            const selectedFrameworks = [];
            const seenFrameworkIds = new Set(); // Track IDs to prevent duplicates

            Object.keys(frameworksByType).forEach(type => {
                const frameworks = frameworksByType[type];
                console.log(`[FrameworkService] Processing type "${type}" with ${frameworks.length} framework(s)`);

                // Find framework matching current language
                let selectedFramework = frameworks.find(f => f.language === currentLanguage);

                // If no exact match, use English as fallback, then any available
                if (!selectedFramework) {
                    selectedFramework = frameworks.find(f => f.language === 'en') || frameworks[0];
                    console.log(`[FrameworkService] No ${currentLanguage} version found for ${type}, using fallback: ${selectedFramework?.name} (${selectedFramework?.language})`);
                } else {
                    console.log(`[FrameworkService] Found ${currentLanguage} version for ${type}: ${selectedFramework.name}`);
                }

                if (selectedFramework) {
                    // Check for duplicate IDs to prevent the same framework being added multiple times
                    const frameworkId = selectedFramework._id || selectedFramework.id;
                    if (seenFrameworkIds.has(frameworkId)) {
                        console.warn(`[FrameworkService] Skipping duplicate framework ID: ${frameworkId} (${selectedFramework.name})`);
                        return;
                    }
                    seenFrameworkIds.add(frameworkId);

                    // Apply any additional transformations if needed
                    const transformedFramework = applyLanguageTransformations(selectedFramework, currentLanguage);

                    // Ensure unique ID is preserved after transformation
                    transformedFramework._id = selectedFramework._id;
                    transformedFramework.id = selectedFramework.id;

                    // Deduplicate rules within the framework
                    if (transformedFramework.rules && Array.isArray(transformedFramework.rules)) {
                        const uniqueRules = [];
                        const seenRuleIds = new Set();

                        transformedFramework.rules.forEach(rule => {
                            if (rule && rule.id && !seenRuleIds.has(rule.id)) {
                                seenRuleIds.add(rule.id);
                                uniqueRules.push(rule);
                            } else if (rule && rule.id) {
                                console.warn(`[FrameworkService] Removing duplicate rule in framework "${transformedFramework.name}": ${rule.name} (ID: ${rule.id})`);
                            }
                        });

                        transformedFramework.rules = uniqueRules;

                        if (uniqueRules.length !== selectedFramework.rules.length) {
                            console.warn(`[FrameworkService] Deduplicated ${selectedFramework.rules.length - uniqueRules.length} rules in framework "${transformedFramework.name}"`);
                        }
                    }

                    selectedFrameworks.push(transformedFramework);
                }
            });

            // Add all custom frameworks individually (no grouping by type)
            customFrameworks.forEach(selectedFramework => {
                const frameworkId = selectedFramework._id || selectedFramework.id;
                if (seenFrameworkIds.has(frameworkId)) {
                    console.warn(`[FrameworkService] Skipping duplicate custom framework ID: ${frameworkId} (${selectedFramework.name})`);
                    return;
                }
                seenFrameworkIds.add(frameworkId);

                const transformedFramework = applyLanguageTransformations(selectedFramework, currentLanguage);
                transformedFramework._id = selectedFramework._id;
                transformedFramework.id = selectedFramework.id;

                if (transformedFramework.rules && Array.isArray(transformedFramework.rules)) {
                    const uniqueRules = [];
                    const seenRuleIds = new Set();
                    transformedFramework.rules.forEach(rule => {
                        if (rule && rule.id && !seenRuleIds.has(rule.id)) {
                            seenRuleIds.add(rule.id);
                            uniqueRules.push(rule);
                        } else if (rule && rule.id) {
                            console.warn(`[FrameworkService] Removing duplicate rule in custom framework "${transformedFramework.name}": ${rule.name} (ID: ${rule.id})`);
                        }
                    });
                    transformedFramework.rules = uniqueRules;
                    if (uniqueRules.length !== selectedFramework.rules.length) {
                        console.warn(`[FrameworkService] Deduplicated ${selectedFramework.rules.length - uniqueRules.length} rules in custom framework "${transformedFramework.name}"`);
                    }
                }

                selectedFrameworks.push(transformedFramework);
            });

            console.log(`[FrameworkService] Selected ${selectedFrameworks.length} frameworks for language ${currentLanguage}:`);
            selectedFrameworks.forEach(f => {
                console.log(`  - ${f.name} (${f.language || 'no language'}) [${f.type || 'no type'}] - ${f.rules?.length || 0} rules`);
            });

            response.data = selectedFrameworks;
        }

        // Assuming your api client wraps the response, adjust if needed
        return response;
    } catch (error) {
        console.error('Error fetching framework definitions:', error);
        // Return error structure consistent with your api client
        return { success: false, message: error.message || 'Failed to fetch framework definitions.' };
    }
};

/**
 * Creates a new custom framework definition linked to an analysis.
 * @param {Object} definitionData - The framework data ({ name, description, rules, analysisId }).
 * @returns {Promise<Object>} - The API response.
 */
const createFrameworkDefinition = async (definitionData) => {
    if (!definitionData || !definitionData.analysisId || !definitionData.name || !definitionData.rules) {
        console.error("createFrameworkDefinition requires definitionData with analysisId, name, and rules.");
        return { success: false, message: "Required definition data is missing." };
    }
    try {
        const response = await api.post(BASE_URL, definitionData);
        return response;
    } catch (error) {
        console.error('Error creating framework definition:', error);
        return { success: false, message: error.message || 'Failed to create framework definition.' };
    }
};

/**
 * Fetches ALL framework definitions (all languages) and creates unified frameworks
 * with type-based stable IDs. This ensures selected rules work regardless of language.
 * @param {string} analysisId - The ID of the analysis.
 * @param {string} currentLanguage - Current display language
 * @returns {Promise<Object>} - Unified framework definitions with type-based IDs
 */
const getAllFrameworkDefinitions = async (analysisId, currentLanguage = null) => {
    if (!analysisId) {
        console.error("getAllFrameworkDefinitions requires an analysisId.");
        return { success: false, message: "Analysis ID is required." };
    }
    try {
        const lang = currentLanguage || i18n.language || 'fr';
        console.log(`[FrameworkService] Fetching ALL framework definitions for language: ${lang}`);

        const response = await api.get(`${BASE_URL}/all`, { analysisId });

        if (!response.data || !Array.isArray(response.data)) {
            return { success: false, message: 'No frameworks found' };
        }

        console.log(`[FrameworkService] Found ${response.data.length} total framework versions`);

        // Group by type, but keep customs separate
        const frameworksByType = {};
        const customFrameworks = [];
        response.data.forEach(framework => {
            const type = framework.type || 'unknown';
            const isCustom = !framework.is_predefined || type === 'custom';
            if (isCustom) {
                customFrameworks.push(framework);
                return;
            }
            if (!frameworksByType[type]) {
                frameworksByType[type] = { fr: null, en: null, all: [] };
            }
            frameworksByType[type].all.push(framework);
            if (framework.language === 'fr') frameworksByType[type].fr = framework;
            if (framework.language === 'en') frameworksByType[type].en = framework;
        });

        // Create unified frameworks
        const unifiedFrameworks = [];
        const languageMapping = {}; // type -> { fr: _id, en: _id }

        // First add predefined grouped (stable order)
        Object.entries(frameworksByType).forEach(([type, data]) => {
            const selected = data[lang] || data.en || data.all[0];
            if (!selected) return;

            const transformed = applyLanguageTransformations(selected, lang);
            const stableId = type;

            const unifiedFramework = {
                ...transformed,
                _id: selected._id,
                id: selected.id,
                stableId: stableId,
                type: type,
                is_predefined: selected.is_predefined,
                languageVersions: {
                    fr: data.fr ? { _id: data.fr._id, id: data.fr.id, name: data.fr.name } : null,
                    en: data.en ? { _id: data.en._id, id: data.en.id, name: data.en.name } : null
                }
            };

            unifiedFrameworks.push(unifiedFramework);
            languageMapping[type] = {
                fr: data.fr ? data.fr._id : null,
                en: data.en ? data.en._id : null
            };
        });

        // Then add all customs individually
        customFrameworks.forEach(selected => {
            const transformed = applyLanguageTransformations(selected, lang);
            const stableId = selected._id || selected.id;
            const unifiedFramework = {
                ...transformed,
                _id: selected._id,
                id: selected.id,
                stableId: stableId,
                type: selected.type || 'custom',
                is_predefined: false,
                languageVersions: null
            };
            unifiedFrameworks.push(unifiedFramework);
        });

        console.log(`[FrameworkService] Created ${unifiedFrameworks.length} unified frameworks`);
        unifiedFrameworks.forEach(f => {
            console.log(`  - ${f.name} (type: ${f.type}, stableId: ${f.stableId})`);
        });

        return {
            success: true,
            data: unifiedFrameworks,
            languageMapping: languageMapping
        };
    } catch (error) {
        console.error('Error fetching all framework definitions:', error);
        return { success: false, message: error.message || 'Failed to fetch framework definitions.' };
    }
};

const updateFrameworkDefinition = async (id, updateData) => {
    if (!id || !updateData) return { success: false, message: 'Missing id or data' };
    try {
        const response = await api.put(`${BASE_URL}/${id}`, updateData);
        return response;
    } catch (e) {
        return { success: false, message: e.message };
    }
};

const deleteFrameworkDefinition = async (id) => {
    if (!id) return { success: false, message: 'Missing id' };
    try {
        const response = await api.delete(`${BASE_URL}/${id}`);
        return response;
    } catch (e) {
        return { success: false, message: e.message };
    }
};

const frameworkDefinitionService = {
    getFrameworkDefinitions,
    getAllFrameworkDefinitions,
    createFrameworkDefinition,
    applyLanguageTransformations,
    updateFrameworkDefinition,
    deleteFrameworkDefinition,
};

export default frameworkDefinitionService;